import { useParams } from "react-router-dom";
import { formatDate } from "devextreme/localization";
import { useStrategicGuide } from "@hooks/useStrategicGuide";
import KeyValuesGrid from "@/components/KeyValuesGrid";
import StrategicGuideSkeleton from "./StrategicGuideSkeleton";
import useHeaderChange from "@hooks/useHeaderChange";
import { useRef } from "react";
import NoDataMessage from "@/components/NoDataMessage";
import { useHouseholdData } from "@/hooks/useHouseholdData";
import { getAnnualReviews, getPlannedRetirementYear } from "./sg-helpers";
import KeyValueSection from "@/components/KeyValueSection";
import { getYesterdayDate } from "@/helper/helper";
import withDisclaimer from "@/hoc/withDisclaimer";
import RebalancesPopup from "./RebalancesPopup";
import { openPopup } from "@/helper/DxHelper";
import { HouseholdDataType } from "@/types/enums";
import PrintStrategicGuideButton from "./PrintStrategicGuideButton";
import StrategyTable from "./StrategyTable";
import { NetPresentValue } from "../NPV/npv-utils";
import { calculateAge } from "../UP/up-utils";
import { PopupRef } from "devextreme-react/cjs/popup";
import { useAppSelector } from "@/hooks/useAppSelector";

function StrategicGuide() {
  const params = useParams();
  const popupRef = useRef<PopupRef>(null);
  const { settings } = useAppSelector((state) => state.npv);
  const { upData } = useHeaderChange(params.id!, "Strategic Guide");

  const {
    data: retirementAge1,
    isLoading: isAge1Loading,
    isRefetching: isAge1Refetching,
  } = useHouseholdData(
    upData?.household?.primaryContact?.externalCode,
    HouseholdDataType.RetirementAge
  );

  const {
    data: retirementAge2,
    isLoading: isAge2Loading,
    isRefetching: isAge2Refetching,
  } = useHouseholdData(
    upData?.household?.secondaryContact?.externalCode,
    HouseholdDataType.RetirementAge
  );

  const {
    data: strategicGuide,
    isLoading: strategicGuideLoading,
    isError,
    isRefetching,
  } = useStrategicGuide(params.id as string);

  if (
    strategicGuideLoading ||
    isRefetching ||
    isAge1Loading ||
    isAge1Refetching ||
    isAge2Loading ||
    isAge2Refetching
  )
    return <StrategicGuideSkeleton />;

  const annualReviews = getAnnualReviews(strategicGuide?.data);

  if (!strategicGuide?.data) return <NoDataMessage />;
  if (isError) return <div>Failed fetching Strategic Guide</div>;

  const {
    federalWithholdings,
    highActionPoint,
    lowActionPoint,
    specialReserves,
    specialReservesNotes,
    targetCash,
    stateWithholdings,
    rebalances,
    sG_AO_FI,
    sG_Non_AO_FI,
    sG_Total_FI,
    strategyGroups,
    shortfall,
  } = strategicGuide.data;

  if (!upData?.household) return;

  const { primaryContact, secondaryContact } = upData.household;

  const plannedRetirementYearPrimary = getPlannedRetirementYear(
    Number(retirementAge1?.data?.data),
    upData?.household.primaryContact?.birthDate
  );

  const plannedRetirementYearSecondary = getPlannedRetirementYear(
    Number(retirementAge2?.data?.data),
    upData?.household.secondaryContact?.birthDate
  );

  const age1 = calculateAge(
    upData?.household?.primaryContact?.birthDate?.toString() ?? "",
    0
  )?.value;

  const age2 = calculateAge(
    upData?.household?.secondaryContact?.birthDate?.toString() ?? "",
    0
  )?.value;

  const { gridSet } = upData?.data ? JSON.parse(upData.data) : { gridSet: [] };
  const npvSettings = upData?.npvSettings ? JSON.parse(upData.npvSettings as string) : settings;
  const { npv } = NetPresentValue.calcHelpers(npvSettings, gridSet, age1, age2);

  const npvSolution =
    upData?.household?.lockedNPV ?? Math.max(0, npv[npvSettings?.npvAge - 5]?.npv ?? 0);
  const abFixedIncome = upData?.household?.clientAdvisorSetABFixedIncome ?? npvSolution;

  return (
    <div className="print-zoom-85 flex flex-col gap-8 text-neutral-100 print:gap-4 print:text-sm">
      <div
        style={{ WebkitPrintColorAdjust: "exact" }}
        className="flex flex-wrap gap-x-6 gap-y-8 rounded-md bg-neutral-500 p-8"
      >
        <KeyValueSection
          outerContainerClasses="w-[calc(50%-1.5rem)]"
          items={[
            { header: "Total AO Fixed Income", value: sG_AO_FI },
            { header: "Total Non-AO Fixed Income", value: sG_Non_AO_FI },
            { header: "Total Fixed Income", value: sG_Total_FI },
          ]}
          sectionHeading="Current Fixed Income"
        />
        <KeyValueSection
          outerContainerClasses="w-[calc(50%-0.5rem)]"
          items={[
            { header: "Target Cash", value: targetCash },
            { header: "High Action Point", value: highActionPoint },
            { header: "Low Action Point", value: lowActionPoint },
          ]}
          sectionHeading="Cash Reserves"
        />
        <KeyValueSection
          outerContainerClasses="w-[calc(50%-1.5rem)]"
          innerContainerClasses="!justify-start gap-12"
          items={[
            { header: "Special Reserves", value: specialReserves },
            { header: "Notes", value: specialReservesNotes },
          ]}
          sectionHeading="BHE Exclusion"
        />
        <KeyValueSection
          outerContainerClasses="w-[calc(25%-1rem)]"
          items={[
            { header: "Federal", value: federalWithholdings / 100, isPrecentage: true },
            { header: "State", value: stateWithholdings / 100, isPrecentage: true },
          ]}
          sectionHeading="Tax Withholding"
          sectionClassName="flex w-[45%]"
        />

        <KeyValueSection
          outerContainerClasses="flex-1"
          sectionClassName="flex w-[45%]"
          items={[
            { header: "AB Fixed Income", value: abFixedIncome, isCurrency: true },
            { header: "Shortfall", value: shortfall, isCurrency: true },
          ]}
          sectionHeading={
            <div className="flex gap-2">
              <span>Rebalance Plan</span>
              <button onClick={() => openPopup(popupRef)}>
                <img src="/icons/eye-icon.svg" />
              </button>
            </div>
          }
        />
      </div>

      {annualReviews?.length ? (
        <div
          style={{ WebkitPrintColorAdjust: "exact" }}
          className={`flex flex-col gap-8 rounded-md bg-neutral-500 p-8`}
        >
          <div className="flex flex-1 flex-col gap-4">
            {Object.entries(strategyGroups).map(([key, value]) => (
              <>
                <h2 className="text-lg font-medium">{key}</h2>
                {value.length > 0 ? <StrategyTable key={key} strategies={value} /> : null}
              </>
            ))}
          </div>

          {annualReviews?.length ? (
            <div className="flex w-1/2 flex-col gap-y-4">
              <h2 className="text-lg font-medium">Annual Client Review Meetings</h2>
              <KeyValuesGrid
                headersClasses="w-1/2"
                headers={["", ...annualReviews.map(({ type }) => type)]}
                valuesClasses="flex-1"
                values={[
                  [
                    <div className="font-medium">Reviewed</div>,
                    ...annualReviews.map(({ lastReview }) =>
                      lastReview ? formatDate(new Date(lastReview), "MM/dd/yyy") : "—"
                    ),
                  ],
                  [
                    <span className="font-medium">Next</span>,
                    ...annualReviews.map(({ nextReview }) => (
                      <span
                        className={`${nextReview && new Date(nextReview) < getYesterdayDate() ? "text-danger" : ""}`}
                      >
                        {nextReview ? formatDate(new Date(nextReview), "MM/dd/yyy") : "—"}
                      </span>
                    )),
                  ],
                ]}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex gap-6">
        {primaryContact?.birthDate && !primaryContact.isDeceased ? (
          <KeyValueSection
            sectionHeading={primaryContact?.preferredFirstName}
            outerContainerClasses="w-1/3 2xl:w-1/4 2xl:min-w-[31.25rem] print:w-full"
            items={[
              { header: "RMD Age", value: primaryContact?.rmdAge, isCurrency: false },
              { header: "RMD Year", value: primaryContact?.rmdYear, isCurrency: false },
              {
                header: "Planned Retirement",
                value: plannedRetirementYearPrimary,
                isCurrency: false,
              },
            ]}
          />
        ) : null}
        {secondaryContact?.birthDate && !secondaryContact?.isDeceased ? (
          <KeyValueSection
            sectionHeading={secondaryContact?.preferredFirstName}
            outerContainerClasses="w-1/3 2xl:w-1/4 2xl:min-w-[31.25rem] print:w-full"
            items={[
              { header: "RMD Age", value: secondaryContact?.rmdAge, isCurrency: false },
              { header: "RMD Year", value: secondaryContact?.rmdYear, isCurrency: false },
              {
                header: "Planned Retirement",
                value: plannedRetirementYearSecondary,
                isCurrency: false,
              },
            ]}
          />
        ) : null}
      </div>
      <RebalancesPopup popupRef={popupRef} rebalances={rebalances} />
      <PrintStrategicGuideButton />
    </div>
  );
}

const SGwithDisclaimer = withDisclaimer(StrategicGuide);

export default SGwithDisclaimer;
